import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminApi, ApiError, fmt,
  type CreditBalanceDto, type CreditLotDto, type CreditQuoteDto, type CreditTierDto,
  type CreditTopupDto, type CreditLedgerEntryDto,
} from '../api';

/**
 * TICK-100 — the organizer's credit surface.
 *
 * Two things on this screen are not decoration and must not be trimmed:
 *
 * - **The forfeit warning.** Trading a still-live lot in for a discount destroys credits the
 *   organizer could still spend. The confirmation states the trade in both units — credits given
 *   up, baht received — because nobody should discover that exchange rate afterwards.
 * - **The expiry-against-event-date notice.** "Credits expire on the 3rd" is a fact; "your event is
 *   on the 12th and your credits expire on the 3rd" is the one an organizer can act on.
 */

const KIND_LABELS: Record<string, string> = {
  purchase: 'ซื้อเครดิต',
  grant: 'ได้รับจากทีมงาน',
  free_tier: 'เครดิตฟรีของอีเวนต์',
  charge: 'ตัดค่าบัตรที่ขายได้',
  uncovered: 'ค้างจ่าย (เครดิตไม่พอ)',
  debt_settle: 'ชำระเครดิตค้างจ่าย',
  expire: 'หมดอายุ',
  carryover_out: 'แลกเป็นส่วนลด',
  carryover_in: 'ส่วนลดที่ได้รับ',
  refund: 'คืนเครดิต',
  admin_adjust: 'ปรับยอดโดยทีมงาน',
};

const SOURCE_LABELS: Record<CreditLotDto['source'], string> = {
  purchase: 'ซื้อ',
  grant: 'ทีมงานให้',
  free_tier: 'ฟรี',
};

const TOPUP_STATUS: Record<CreditTopupDto['status'], string> = {
  pending: 'รอแนบสลิป',
  awaiting_verification: 'รอตรวจสอบ',
  confirmed: 'เติมเครดิตแล้ว',
  rejected: 'ถูกปฏิเสธ',
  expired: 'หมดอายุใบเสนอราคา',
};

function expiryLabel(lot: CreditLotDto): string {
  if (lot.daysUntilExpiry === null) return 'ไม่มีวันหมดอายุ';
  if (lot.daysUntilExpiry < 0) return `หมดอายุแล้ว ${Math.abs(lot.daysUntilExpiry)} วัน`;
  if (lot.daysUntilExpiry === 0) return 'หมดอายุวันนี้';
  return `เหลืออีก ${lot.daysUntilExpiry} วัน`;
}

export function CreditsPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [balance, setBalance] = useState<CreditBalanceDto | null>(null);
  const [tiers, setTiers] = useState<CreditTierDto[]>([]);
  const [topups, setTopups] = useState<CreditTopupDto[]>([]);
  const [statement, setStatement] = useState<CreditLedgerEntryDto[]>([]);
  const [error, setError] = useState('');

  const [buying, setBuying] = useState(false);
  const [picks, setPicks] = useState<Record<string, number>>({});
  const [tradeIn, setTradeIn] = useState<string[]>([]);
  const [quote, setQuote] = useState<CreditQuoteDto | null>(null);
  const [buyErr, setBuyErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [showStatement, setShowStatement] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, t, u] = await Promise.all([
        adminApi.getCredits(token, eventSlug),
        adminApi.listCreditTiers(token, eventSlug),
        adminApi.listCreditTopups(token, eventSlug),
      ]);
      setBalance(b);
      setTiers(t);
      setTopups(u);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'โหลดข้อมูลเครดิตไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(
    () => Object.entries(picks).filter(([, q]) => q > 0).map(([tierId, quantity]) => ({ tierId, quantity })),
    [picks],
  );

  // Re-priced by the server on every change: the discount cap depends on the purchase total, so a
  // client-side estimate would disagree with the quote the organizer is actually offered.
  useEffect(() => {
    if (!buying || items.length === 0) { setQuote(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const q = await adminApi.quoteCredits(token, items, tradeIn, eventSlug);
        if (!cancelled) { setQuote(q); setBuyErr(''); }
      } catch (e) {
        if (!cancelled) { setQuote(null); setBuyErr(e instanceof ApiError ? e.message : 'คำนวณราคาไม่สำเร็จ'); }
      }
    })();
    return () => { cancelled = true; };
  }, [buying, items, tradeIn, token, eventSlug]);

  const eligibleLots = (balance?.lots ?? []).filter((l) => l.carryoverEligible && l.creditsRemaining > 0 && !l.closedAt);

  const setPick = (tierId: string, quantity: number) =>
    setPicks((p) => ({ ...p, [tierId]: Math.max(0, Math.min(50, quantity)) }));

  const toggleTradeIn = (lotId: string) =>
    setTradeIn((cur) => (cur.includes(lotId) ? cur.filter((id) => id !== lotId) : [...cur, lotId]));

  const submit = async () => {
    if (!items.length) return;
    setBusy(true);
    setBuyErr('');
    try {
      await adminApi.createCreditTopup(token, items, tradeIn, eventSlug);
      setBuying(false);
      setPicks({});
      setTradeIn([]);
      setQuote(null);
      await load();
    } catch (e) {
      setBuyErr(e instanceof ApiError ? e.message : 'สร้างรายการเติมเครดิตไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const uploadSlip = async (topupId: string, file: File) => {
    setBusy(true);
    try {
      await adminApi.uploadCreditTopupSlip(token, topupId, file, eventSlug);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'อัปโหลดสลิปไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const openStatement = async () => {
    setShowStatement((v) => !v);
    if (statement.length === 0) {
      try {
        setStatement(await adminApi.getCreditStatement(token, eventSlug));
      } catch {
        setError('โหลดรายการเดินบัญชีไม่สำเร็จ');
      }
    }
  };

  if (error && !balance) return <div className="form-error">{error}</div>;
  if (!balance) return <div className="admin-group">กำลังโหลด…</div>;

  const eventEnd = balance.eventEndsAt ? new Date(balance.eventEndsAt) : null;
  const trading = eligibleLots.filter((l) => tradeIn.includes(l.id));
  const tradingLiveCredits = trading.filter((l) => l.live).reduce((s, l) => s + l.creditsRemaining, 0);

  return (
    <div>
      {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

      {!balance.billingEnabled && (
        <div className="admin-group" style={{ marginBottom: 12 }}>
          อีเวนต์นี้ยังไม่ได้เปิดใช้ระบบเครดิต — บัตรที่ขายได้จะยังไม่ถูกตัดเครดิต
        </div>
      )}

      {/* --- balance ------------------------------------------------------ */}
      <div className="admin-group">
        <div className="card-step">เครดิตคงเหลือ</div>
        <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.1, marginTop: 6 }}>
          {balance.spendable.toLocaleString('en-US')} <span style={{ fontSize: 15, fontWeight: 400 }}>ใบ</span>
        </div>
        <div style={{ opacity: 0.7, fontSize: 13, marginTop: 4 }}>
          1 เครดิต = ผู้เข้างาน 1 คน · ตัดเมื่อออเดอร์ชำระเงินสำเร็จ
        </div>

        {balance.debtCredits > 0 && (
          <div className="form-error" style={{ marginTop: 12 }}>
            ค้างจ่าย {balance.debtCredits.toLocaleString('en-US')} ใบ — ระบบไม่ได้หยุดการขาย
            แต่จะหักจากยอดเติมครั้งถัดไปก่อน
          </div>
        )}

        {balance.expiringSoon.length > 0 && (
          <div className="admin-group" style={{ marginTop: 12 }}>
            <strong>ใกล้หมดอายุ</strong>
            {balance.expiringSoon.map((l) => {
              // The warning worth reading is the collision, not the date. An organizer already
              // knows when their event is; what they cannot see is that their credits die first.
              const diesBeforeEvent = !!(eventEnd && l.expiresAt && new Date(l.expiresAt) < eventEnd);
              return (
                <div key={l.id} style={{ marginTop: 6, fontSize: 13 }}>
                  {l.creditsRemaining.toLocaleString('en-US')} ใบ · {expiryLabel(l)}
                  {l.expiresAt ? ` (${new Date(l.expiresAt).toLocaleDateString('th-TH')})` : ''}
                  {diesBeforeEvent && (
                    <strong style={{ display: 'block', color: 'var(--sv-danger)' }}>
                      งาน{balance.eventName ? ` ${balance.eventName}` : ''} วันที่ {eventEnd!.toLocaleDateString('th-TH')}
                      {' '}— เครดิตชุดนี้หมดอายุก่อนงานจบ
                    </strong>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="admin-actions" style={{ marginTop: 14 }}>
          <button className="btn-primary" style={{ padding: '9px 18px' }} onClick={() => setBuying((v) => !v)}>
            {buying ? 'ปิดหน้าซื้อเครดิต' : 'ซื้อเครดิต'}
          </button>
          <button className="btn-ghost" onClick={openStatement}>
            {showStatement ? 'ซ่อนรายการเดินบัญชี' : 'ดูรายการเดินบัญชี'}
          </button>
        </div>
      </div>

      {/* --- buy ---------------------------------------------------------- */}
      {buying && (
        <div className="admin-group" style={{ marginTop: 12 }}>
          <div className="card-step">เลือกแพ็กเกจ (ผสมได้)</div>
          {tiers.map((t) => (
            <div key={t.id} className="admin-group" style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <strong>{t.credits.toLocaleString('en-US')} เครดิต</strong>
                  <div style={{ fontSize: 13, opacity: 0.75 }}>
                    {fmt(t.unitPriceThb)}/ใบ · รวม {fmt(t.grossThb)} · ใช้ได้ {t.validDays} วัน
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn-ghost" onClick={() => setPick(t.id, (picks[t.id] ?? 0) - 1)}>−</button>
                  <span style={{ minWidth: 24, textAlign: 'center' }}>{picks[t.id] ?? 0}</span>
                  <button className="btn-ghost" onClick={() => setPick(t.id, (picks[t.id] ?? 0) + 1)}>+</button>
                </div>
              </div>
            </div>
          ))}

          {eligibleLots.length > 0 && (
            <div className="admin-group" style={{ marginTop: 14 }}>
              <div className="card-step">แลกเครดิตคงเหลือเป็นส่วนลด</div>
              <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 8 }}>
                คืนให้ 50% ของมูลค่า สูงสุดไม่เกิน 30% ของยอดซื้อใหม่ · แลกได้ภายใน 60 วันหลังหมดอายุ
              </div>
              {eligibleLots.map((l) => (
                <label key={l.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={tradeIn.includes(l.id)} onChange={() => toggleTradeIn(l.id)} />
                  <span style={{ fontSize: 13 }}>
                    {l.creditsRemaining.toLocaleString('en-US')} ใบ · มูลค่า {fmt(l.remainingValueThb)} · {expiryLabel(l)}
                    {l.live && (
                      <strong style={{ display: 'block', color: 'var(--sv-danger)' }}>
                        ยังใช้งานได้อยู่ — แลกแล้วเครดิตที่เหลือจะถูกปิดทันที
                      </strong>
                    )}
                  </span>
                </label>
              ))}
            </div>
          )}

          {quote && (
            <div className="admin-group" style={{ marginTop: 14 }}>
              <div>ได้รับ <strong>{quote.totalCredits.toLocaleString('en-US')}</strong> เครดิต</div>
              <div style={{ marginTop: 4 }}>ราคาเต็ม {fmt(quote.grossThb)}</div>
              {quote.carryoverDiscountThb > 0 && (
                <div style={{ marginTop: 4 }}>
                  ส่วนลดจากเครดิตคงเหลือ −{fmt(quote.carryoverDiscountThb)}
                  {quote.carryoverDiscountThb < Math.floor(quote.carryoverValueThb * 0.5) && (
                    <span style={{ opacity: 0.7 }}> (ติดเพดาน 30% ของยอดซื้อ)</span>
                  )}
                </div>
              )}
              {tradingLiveCredits > 0 && (
                <div className="form-error" style={{ marginTop: 8 }}>
                  จะเสียเครดิตที่ยังใช้ได้ {tradingLiveCredits.toLocaleString('en-US')} ใบ
                  เพื่อแลกส่วนลด {fmt(quote.carryoverDiscountThb)}
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 20, fontWeight: 700 }}>ยอดที่ต้องโอน {fmt(quote.amountDueThb)}</div>

              {quote.betterTier && (
                <div className="admin-group" style={{ marginTop: 12 }}>
                  แพ็กเกจ {quote.betterTier.credits.toLocaleString('en-US')} เครดิต ราคา {fmt(quote.betterTier.grossThb)}
                  {' '}<strong>ถูกกว่าและได้มากกว่า</strong> — ใช้ได้ {quote.betterTier.validDays} วัน
                  <div style={{ marginTop: 8 }}>
                    <button
                      className="btn-ghost"
                      onClick={() => { setPicks({ [quote.betterTier!.id]: 1 }); }}
                    >
                      เปลี่ยนเป็นแพ็กเกจนี้
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {buyErr && <div className="form-error" style={{ marginTop: 12 }}>{buyErr}</div>}
          <div className="admin-actions" style={{ marginTop: 14 }}>
            <button className="btn-primary" style={{ padding: '9px 18px' }} disabled={!items.length || busy} onClick={submit}>
              สร้างรายการและรับเลขบัญชี
            </button>
            <button className="btn-ghost" onClick={() => { setBuying(false); setPicks({}); setTradeIn([]); }}>ยกเลิก</button>
          </div>
        </div>
      )}

      {/* --- top-ups ------------------------------------------------------ */}
      {topups.length > 0 && (
        <div className="admin-group" style={{ marginTop: 12 }}>
          <div className="card-step">รายการเติมเครดิต</div>
          {topups.map((t) => (
            <div key={t.id} className="admin-group" style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <strong>{fmt(t.amountDueThb)}</strong>
                  {t.carryoverDiscountThb > 0 && (
                    <span style={{ opacity: 0.7 }}> (หลังหักส่วนลด {fmt(t.carryoverDiscountThb)})</span>
                  )}
                  <div style={{ fontSize: 13, opacity: 0.75 }}>
                    {new Date(t.createdAt).toLocaleString('th-TH')} · {TOPUP_STATUS[t.status]}
                  </div>
                </div>
                {t.status === 'pending' && (
                  <label className="btn-ghost" style={{ display: 'inline-block', cursor: 'pointer' }}>
                    แนบสลิปโอนเงิน
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadSlip(t.id, file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- lots --------------------------------------------------------- */}
      <div className="admin-group" style={{ marginTop: 12 }}>
        <div className="card-step">เครดิตแต่ละก้อน</div>
        {balance.lots.length === 0 && <div style={{ opacity: 0.7 }}>ยังไม่มีเครดิต</div>}
        {balance.lots.map((l) => (
          <div key={l.id} style={{ marginTop: 10, fontSize: 13 }}>
            <strong>{l.creditsRemaining.toLocaleString('en-US')}</strong> / {l.creditsTotal.toLocaleString('en-US')} ใบ
            {' · '}{SOURCE_LABELS[l.source]}
            {l.unitPriceThb > 0 && ` · ${fmt(l.unitPriceThb)}/ใบ`}
            {' · '}{expiryLabel(l)}
          </div>
        ))}
      </div>

      {/* --- statement ---------------------------------------------------- */}
      {showStatement && (
        <div className="admin-group" style={{ marginTop: 12 }}>
          <div className="card-step">รายการเดินบัญชี</div>
          {statement.length === 0 && <div style={{ opacity: 0.7 }}>ยังไม่มีรายการ</div>}
          {statement.map((row) => (
            <div key={row.id} style={{ marginTop: 8, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span>
                {KIND_LABELS[row.kind] ?? row.kind}
                {row.note && <span style={{ opacity: 0.7 }}> — {row.note}</span>}
                <span style={{ display: 'block', opacity: 0.6 }}>{new Date(row.created_at).toLocaleString('th-TH')}</span>
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>
                {row.delta !== 0 && `${row.delta > 0 ? '+' : ''}${row.delta.toLocaleString('en-US')} ใบ`}
                {row.amount_thb > 0 && ` ${fmt(row.amount_thb)}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
